import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const getCurrentUser = () => localStorage.getItem('notes_user') || '';

async function fetchWithAuth(url, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${url}`, { ...options, headers: { ...options.headers, 'x-token': token } });
  if (res.status === 401) { localStorage.removeItem('notes_token'); localStorage.removeItem('notes_user'); alert('登录超时，请重新登录'); window.location.href = '/'; return null; }
  return res;
}

function downloadFile(url, filename, tplId, taskId) {
  if (tplId && taskId) {
    // 通过后端代理下载（COS私有文件）
    window.open(`${API_URL}/api/tasks/${taskId}/templates/${tplId}/download?token=${getToken()}`);
  } else {
    fetch(url).then(r => r.blob()).then(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    });
  }
}

export default function TaskMaintenance() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterType, setFilterType] = useState(''); // 筛选类型
  const [filterProject, setFilterProject] = useState(''); // 项目筛选
  const [projects, setProjects] = useState([]); // 所有项目列表
  const [completingPeriod, setCompletingPeriod] = useState(false);

  const [form, setForm] = useState({ status: '', progress: 0, actualStart: '', actualEnd: '', workNotes: '', currentPeriod: 1 });
  const [templates, setTemplates] = useState([]);
  const [viewPeriod, setViewPeriod] = useState(null); // null=当前期, 数字=查看的历史期
  const [editNotes, setEditNotes] = useState(''); // 编辑历史期的workNotes
  const [editAttachments, setEditAttachments] = useState([]); // 编辑历史期的attachments
  const [savingHistory, setSavingHistory] = useState(false);

  useEffect(() => { fetchTask(); fetchUsers(); }, [taskId]);
  useEffect(() => { if (filterType) fetchTask(); }, [filterType, filterProject]);

  const fetchTask = async (type, projId) => {
    const ft = type || filterType;
    const pid = projId || filterProject;
    setLoading(true);
    
    // 先尝试从 my-todo 获取
    const res = await fetchWithAuth('/api/tasks/my-todo');
    if (!res) return;
    const myTodos = await res.json();
    let found = myTodos.find(t => t.id === taskId);
    
    // 如果找不到，直接获取任务详情（允许查看他人任务）
    if (!found) {
      const detailRes = await fetchWithAuth(`/api/tasks/${taskId}`);
      if (detailRes) {
        found = await detailRes.json();
      }
    }
    
    if (found) {
      const projRes = await fetchWithAuth(`/api/projects/${found.projectId}`);
      const proj = projRes ? await projRes.json() : null;
      setProject(proj);
      setTask(found);
      setForm({ status: found.status || '', progress: found.progress || 0, actualStart: found.actualStart?.split('T')[0] || '', actualEnd: found.actualEnd?.split('T')[0] || '', workNotes: found.workNotes || '', currentPeriod: found.currentPeriod || 1 });
      setTemplates(found.templates || []);
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    const res = await fetchWithAuth('/api/user/list');
    if (!res) return;
    setUsers(await res.json() || []);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetchWithAuth(`/api/tasks/${taskId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data = await res.json();
      if (data.error) { alert(data.error); return; }
      alert('保存成功');
      fetchTask();
    } catch (e) { alert('保存失败'); }
    setSaving(false);
  };

  const handleCompletePeriod = async () => {
    if (!window.confirm(`确认完成第 ${form.currentPeriod} 期？完成后将自动进入第 ${form.currentPeriod + 1} 期。`)) return;
    setCompletingPeriod(true);
    try {
      const res = await fetchWithAuth(`/api/tasks/${taskId}/complete-period`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ period: form.currentPeriod, workNotes: form.workNotes }) });
      const data = await res.json();
      if (data.error) { alert(data.error); return; }
      alert(`第 ${form.currentPeriod} 期已完成！`);
      navigate('/'); // 跳转首页
    } catch (e) { alert('操作失败'); }
    setCompletingPeriod(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetchWithAuth(`/api/tasks/${taskId}/attachments`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.error) { alert(data.error); return; }
      fetchTask();
    } catch (err) { alert('上传失败'); }
    setUploading(false);
    e.target.value = '';
  };

  const handleDeleteAttachment = async (attId) => {
    if (!window.confirm('确定删除此附件？')) return;
    try { await fetchWithAuth(`/api/tasks/${taskId}/attachments/${attId}`, { method: 'DELETE' }); fetchTask(); } catch (err) { alert('删除失败'); }
  };

  const getUserRealname = (username) => { const u = users.find(u => u.username === username); return u ? u.realname : username; };
  const getStatusStyle = (status) => {
    switch (status) {
      case '已完成': return { bg: '#f6ffed', color: '#52c41a', border: '#b7eb8f' };
      case '进行中': return { bg: '#e6f7ff', color: '#1890ff', border: '#91d5ff' };
      case '已延期': return { bg: '#fff2f0', color: '#ff4d4f', border: '#ffa39e' };
      default: return { bg: '#f5f5f5', color: '#8c8c8c', border: '#d9d9d9' };
    }
  };
  const formatSize = (bytes) => { if (bytes < 1024) return bytes + 'B'; if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB'; return (bytes / (1024 * 1024)).toFixed(1) + 'MB'; };

  if (loading) return <div className="loading-state">加载中...</div>;
  if (!task) return <div className="empty-state"><h2>任务不存在</h2><button className="btn-secondary" onClick={() => navigate(-1)}>返回</button></div>;

  const statusStyle = getStatusStyle(task.status);
  const isRecurring = task.isRecurring;
  const canEdit = project?.owner === getCurrentUser() || task.assignee === getCurrentUser() || getCurrentUser() === 'jch';

  return (
    <div className="page-container">
      {/* 顶部导航 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className="btn-secondary" onClick={() => navigate(-1)} style={{ padding: '4px 12px' }}>← 返回</button>
        <span style={{ color: '#999' }}>{project?.name || ''}</span>
      </div>

      {/* 任务标题 */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 22 }}>{task.name}</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ padding: '2px 10px', borderRadius: 4, fontSize: 13, background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>{task.status}</span>
          <span style={{ color: '#666' }}>负责人：{getUserRealname(task.assignee)}</span>
          {task.priority && <span style={{ color: '#666' }}>优先级：{task.priority}</span>}
          {isRecurring && <span style={{ padding: '2px 10px', borderRadius: 4, fontSize: 13, background: '#f0f5ff', color: '#2f54eb', border: '1px solid #adc6ff' }}>🔄 周期任务</span>}
        </div>
      </div>

      {/* 计划信息 */}
      <div style={{ background: '#fafafa', borderRadius: 8, padding: '16px 20px', marginBottom: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
        <div>
          <div style={{ color: '#999', fontSize: 13, marginBottom: 4 }}>计划开始</div>
          <div>{task.plannedStart || '-'}</div>
        </div>
        <div>
          <div style={{ color: '#999', fontSize: 13, marginBottom: 4 }}>计划结束</div>
          <div style={{ color: task.status === '已延期' ? '#ff4d4f' : 'inherit' }}>{task.plannedEnd || '-'}</div>
        </div>
        {isRecurring && (
          <div>
            <div style={{ color: '#999', fontSize: 13, marginBottom: 4 }}>{viewPeriod ? '查看期数' : '当前期数'}</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: viewPeriod ? '#d48806' : '#2f54eb' }}>第 {viewPeriod || form.currentPeriod} 期</div>
          </div>
        )}
      </div>

      {/* 备注 */}
      {task.remarks && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, marginBottom: 8, color: '#333' }}>📋 备注</h3>
          <div style={{ background: '#fffbe6', borderRadius: 8, padding: '12px 16px', borderLeft: '3px solid #faad14', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{task.remarks}</div>
        </div>
      )}

      {/* 返回当前期按钮（查看历史期时显示） */}
      {viewPeriod !== null && (
        <div style={{ marginBottom: 20 }}>
          <button onClick={() => setViewPeriod(null)} style={{ padding: '6px 16px', background: '#f0f0f0', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>← 返回当前期（第 {form.currentPeriod} 期）</button>
        </div>
      )}

      {/* 模板下载 */}
      {templates.filter(tpl => tpl.period === (viewPeriod || form.currentPeriod)).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, marginBottom: 12, color: '#333' }}>📥 第{viewPeriod || form.currentPeriod}期任务模板</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {templates.filter(tpl => tpl.period === (viewPeriod || form.currentPeriod)).map(tpl => (
              <div key={tpl.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', background: '#e6f7ff', borderRadius: 6, border: '1px solid #91d5ff' }}>
                <span onClick={() => downloadFile(tpl.cosUrl || `${API_URL}${tpl.url}`, tpl.originalName, tpl.id, taskId)} style={{ color: '#1890ff', cursor: 'pointer' }}>
                  📥 {tpl.originalName} <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>{formatSize(tpl.size)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 附件区域 */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, margin: 0, color: '#333' }}>📎 {viewPeriod ? `第${viewPeriod}期附件` : '附件'}</h3>
          {viewPeriod === null && canEdit && (
            <label style={{ padding: '6px 16px', background: '#1890ff', color: '#fff', borderRadius: 6, cursor: uploading ? 'not-allowed' : 'pointer', fontSize: 13 }}>
              {uploading ? '上传中...' : '+ 上传附件'}
              <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
            </label>
          )}
        </div>
        {viewPeriod === null && <p style={{ color: '#faad14', fontSize: 13, marginBottom: 12 }}>💡 请先上传附件，再完成任务</p>}
        {/* 当前期的附件 */}
        {viewPeriod === null && task.attachments && task.attachments.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {task.attachments.map(att => (
              <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                <span onClick={() => downloadFile(att.cosUrl || `${API_URL}${att.url}`, att.originalName)} style={{ color: '#1890ff', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  📄 {att.originalName} <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>{formatSize(att.size)}</span>
                </span>
                {canEdit && <button onClick={() => handleDeleteAttachment(att.id)} style={{ color: '#ff4d4f', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontSize: 13 }}>删除</button>}
              </div>
            ))}
          </div>
        )}
        {/* 历史期的附件 */}
        {viewPeriod !== null && (() => {
          const record = task.periodRecords?.find(r => r.period === viewPeriod);
          const attachments = record?.attachments || [];
          const canEditHistory = viewPeriod === form.currentPeriod - 1 && canEdit;
          return (
            <>
              {attachments.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {attachments.map(att => (
                    <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                      <span onClick={() => downloadFile(att.cosUrl || `${API_URL}${att.url}`, att.originalName)} style={{ color: '#1890ff', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        📄 {att.originalName} <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>{formatSize(att.size)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>暂无附件</div>}
            </>
          );
        })()}
        {viewPeriod === null && task.attachments?.length === 0 && <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>暂无附件</div>}
      </div>

      {/* 任务维护表单 */}
      {viewPeriod === null && canEdit && (
        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: 24, marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, marginBottom: 20, color: '#333' }}>📝 任务维护</h3>

          {/* 周期任务：只有完成按钮和工作说明 */}
          {isRecurring ? (
            <>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: '#555' }}>工作说明</label>
                <textarea value={form.workNotes} onChange={e => setForm({ ...form, workNotes: e.target.value })} placeholder="记录本期工作内容、遇到的问题、解决方案等..." rows={6} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d9d9d9', borderRadius: 6, lineHeight: 1.6, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <button onClick={handleCompletePeriod} disabled={completingPeriod} style={{ padding: '10px 32px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 6, cursor: completingPeriod ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 500 }}>
                {completingPeriod ? '提交中...' : `✅ 完成本期（第 ${form.currentPeriod} 期）`}
              </button>
            </>
          ) : (
            <>
              {/* 非周期任务：完整表单 */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: '#555' }}>状态</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 6 }}>
                    <option value="未开始">未开始</option><option value="进行中">进行中</option><option value="已延期">已延期</option><option value="已完成">已完成</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: '#555' }}>进度</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="range" min="0" max="100" value={form.progress} onChange={e => { const p = Number(e.target.value); setForm({ ...form, progress: p, status: p === 100 ? '已完成' : p > 0 ? '进行中' : form.status }); }} style={{ flex: 1 }} />
                    <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 600 }}>{form.progress}%</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: '#555' }}>实际开始日期</label>
                  <input type="date" value={form.actualStart} onChange={e => setForm({ ...form, actualStart: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: '#555' }}>实际完成日期</label>
                  <input type="date" value={form.actualEnd} onChange={e => setForm({ ...form, actualEnd: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 6 }} />
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: '#555' }}>工作说明</label>
                <textarea value={form.workNotes} onChange={e => setForm({ ...form, workNotes: e.target.value })} placeholder="记录工作内容、遇到的问题、解决方案等..." rows={6} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d9d9d9', borderRadius: 6, lineHeight: 1.6, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              <button onClick={handleSave} disabled={saving} style={{ padding: '8px 24px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 500 }}>
                {saving ? '保存中...' : '💾 保存'}
              </button>
            </>
          )}
        </div>
      )}

      {/* 历史期编辑区域（仅查看上一期时显示） */}
      {viewPeriod === form.currentPeriod - 1 && canEdit && (() => {
        const record = task.periodRecords?.find(r => r.period === viewPeriod);
        if (!record) return null;
        return (
          <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: 24, marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, marginBottom: 16, color: '#d48806' }}>✏️ 编辑第{viewPeriod}期</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: '#555' }}>工作说明</label>
              <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={4} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d9d9d9', borderRadius: 6, lineHeight: 1.6, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ display: 'block', fontSize: 14, color: '#555' }}>附件管理</label>
                <label style={{ padding: '4px 12px', background: '#1890ff', color: '#fff', borderRadius: 4, cursor: savingHistory ? 'not-allowed' : 'pointer', fontSize: 12 }}>
                  + 上传附件
                  <input type="file" style={{ display: 'none' }} onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    e.target.value = '';
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      const res = await fetchWithAuth(`/api/tasks/${taskId}/attachments`, { method: 'POST', body: formData });
                      const data = await res.json();
                      if (data && data.id) {
                        setEditAttachments(prev => [...prev, data]);
                        // 从当前期附件中移除（上传接口默认加到task.attachments）
                        try { await fetchWithAuth(`/api/tasks/${taskId}/attachments/${data.id}`, { method: 'DELETE' }); } catch(e) {}
                        fetchTask(); // 刷新task数据，移除当前期残留
                      } else { alert('上传失败'); }
                    } catch (err) { alert('上传失败'); }
                  }} disabled={savingHistory} />
                </label>
              </div>
              {editAttachments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                  {editAttachments.map(att => (
                    <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#fff', borderRadius: 4, border: '1px solid #f0f0f0' }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>📄 {att.originalName}</span>
                      <button onClick={() => setEditAttachments(prev => prev.filter(a => a.id !== att.id))} style={{ color: '#ff4d4f', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>删除</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={async () => {
              setSavingHistory(true);
              try {
                const res = await fetchWithAuth(`/api/tasks/${taskId}/period/${viewPeriod}/update`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ workNotes: editNotes, attachments: editAttachments })
                });
                const data = await res.json();
                if (data.success) {
                  alert('保存成功');
                  fetchTask();
                } else {
                  alert(data.error || '保存失败');
                }
              } catch (e) { alert('保存失败'); }
              setSavingHistory(false);
            }} disabled={savingHistory} style={{ padding: '8px 24px', background: '#fa8c16', color: '#fff', border: 'none', borderRadius: 6, cursor: savingHistory ? 'not-allowed' : 'pointer', fontSize: 14 }}>
              {savingHistory ? '保存中...' : '💾 保存修改'}
            </button>
          </div>
        );
      })()}

      {/* 周期任务历史记录 */}
      {isRecurring && task.periodRecords && task.periodRecords.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, marginBottom: 12, color: '#333' }}>📊 期数记录</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[...task.periodRecords].reverse().map(r => (
              <div key={r.period} onClick={() => {
                setViewPeriod(r.period);
                if (r.period === form.currentPeriod - 1) {
                  setEditNotes(r.workNotes || '');
                  setEditAttachments(r.attachments || []);
                }
              }} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: viewPeriod === r.period ? '#bae7ff' : '#f6ffed', borderRadius: 6, border: `1px solid ${viewPeriod === r.period ? '#69c0ff' : '#b7eb8f'}`, cursor: 'pointer', transition: 'all 0.2s' }}>
                <span style={{ fontWeight: 600, color: '#52c41a', minWidth: 70 }}>第 {r.period} 期</span>
                <span style={{ color: '#52c41a' }}>✅ 已完成</span>
                <span style={{ color: '#999', fontSize: 12 }}>{r.completedAt?.replace('T', ' ').substring(0, 16)}</span>
                {r.workNotes && <span style={{ color: '#666', fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.workNotes}</span>}
                {r.period === form.currentPeriod - 1 && <span style={{ padding: '1px 6px', background: '#fff7e6', color: '#d48806', borderRadius: 3, fontSize: 11 }}>可编辑</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
